/* eslint-disable max-lines-per-function */
import React from "react";
import { NavigateFn } from "@reach/router";
import { useQuery } from "@apollo/client";
import { values } from "lodash";
import css from "classnames";
import {
  FetchInterestDocuments,
  FetchInterestDocumentsVariables,
} from "types/gql-types";
import { ALLOWED_EXTENSIONS, MAXIMUM_FILE_SIZE } from "../constants";
import { INTEREST_DOCUMENTS } from "../query";
import { reducer } from "../reducer";
import { validate } from "../utils";
import Documents from "./documents";
import DocumentRow from "./document-row";
import DocumentUploader from "./document-uploader";
import styles from "./index.module.css";


interface Props {
  path: string;
  interestID: string;
  enabled: boolean;
  location?: {
    state: {
      addDocumentIndex?: number
    },
    pathname: string
  };

  navigate?: NavigateFn;
}


const Dashboard: React.FC<Props> = (props) => {

  // GraphQL query for fetching documents related to the current interest.
  const variables = { interestID: props.interestID };
  const query = useQuery<FetchInterestDocuments, FetchInterestDocumentsVariables>(
    INTEREST_DOCUMENTS,
    {
      fetchPolicy: "network-only",
      variables,
    },
  );

  // Local state and associated dispatcher for managing the state associated
  // with selecting documents from the local filesystem, selecting categories,
  // and any related request statuses.
  const [state, dispatch] = React.useReducer(reducer, {});

  // The local selected files state modified with any potential validation
  // issues.
  const docs = values(validate(state));

  return (
    <>
      <div className="panel panel-default">
        <div className="panel-heading">Upload new documents</div>

        <div className="panel-body">
          <form>
            <DocumentUploader
              docs={docs}
              state={state}
              dispatch={dispatch}
              interestID={props.interestID}
              query={query}
              navigate={props.navigate}
              location={props.location}
            />

            <div className="form-group" style={{ overflowX: "auto" }}>
              {docs.length > 0 && (
                <table className={css("table", "table-condensed", styles.table)}>
                  <thead>
                    <tr>
                      <th></th>
                      <th>Filename</th>
                      <th>Category</th>
                      <th>Size</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {docs.map((document) => (
                      <DocumentRow key={document.id} document={document} dispatch={dispatch} />
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </form>

          <div className="panel panel-warning" style={{ marginBottom: 0 }}>
            <div className="panel-heading">File limits</div>
            <div className="panel-body">
              <table className={css(styles.rules, "table")}>
                <tbody>
                  <tr>
                    <td>Allowed file types</td>
                    <td>
                      {ALLOWED_EXTENSIONS.map((ext) => (
                        <React.Fragment key={ext}>
                          <span className="label label-default">{ext}</span>&nbsp;
                        </React.Fragment>
                      ))}
                    </td>
                  </tr>
                  <tr>
                    <td>Maximum file size</td>
                    <td>{MAXIMUM_FILE_SIZE / 1024 / 1024}MB</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="panel panel-default">
        <div className="panel-heading">Documents</div>
        <div className="panel-body">
          <Documents interestID={props.interestID} query={query} />
        </div>
      </div>
    </>
  );
};

const Container: React.FC<Props> = (props) => {
  const { enabled } = props;
  if (!enabled) {
    return (
      <div className="panel panel-default">
        <div className="panel-heading">Documents</div>
        <div className="panel-body">Document uploads are not enabled for your property.</div>
      </div>
    );
  }

  return <Dashboard {...props} />;
};

export default Container;
